import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Close } from '@material-ui/icons';

// Fonctions utilitaires pour l'éclaircissement des images
const lightenImage = (imageFile, lightenFactor = 1.3) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        data[i] = Math.min(255, data[i] * lightenFactor);
        data[i + 1] = Math.min(255, data[i + 1] * lightenFactor);
        data[i + 2] = Math.min(255, data[i + 2] * lightenFactor);
      }

      ctx.putImageData(imageData, 0, 0);
      canvas.toBlob(resolve, 'image/png');
    };

    img.src = imageFile;
  });
};

const isImageTooDark = (imageFile, threshold = 80) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      let totalBrightness = 0;
      let pixelCount = 0;

      for (let i = 0; i < data.length; i += 4) {
        const brightness = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        totalBrightness += brightness;
        pixelCount++;
      }

      const averageBrightness = totalBrightness / pixelCount;
      resolve(averageBrightness < threshold);
    };

    img.src = imageFile;
  });
};

/**
 * Affiche une modale de découpe d'image avec éclaircissement automatique.
 * @param {string} src URL de l'image à découper.
 * @param {function(string)} onCrop Fonction appelée lorsque l'utilisateur
 *     confirme la zone de découpe. La fonction prend en argument l'URL de
 *     l'image découpée.
 * @param {function()} onCancel Fonction appelée lorsque l'utilisateur
 *     annule la modale.
 */
const ImageCropper = ({ src, onCrop, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [rotationAngle, setRotationAngle] = useState(0);
  const [currentImageSrc, setCurrentImageSrc] = useState(src);
  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState(null);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // Fonction pour afficher les notifications
  const showNotification = (type, message, duration = 3000) => {
    setNotification({ type, message });
    if (duration > 0) {
      setTimeout(() => {
        setNotification(null);
      }, duration);
    }
  };

  const drawCanvas = useCallback(() => {
    if (!imageLoaded) return;
    const img = imageRef.current;
    const canvas = canvasRef.current;
    const container = containerRef.current;

    if (img && canvas && container) {
      const angleRad = (rotationAngle * Math.PI) / 180;
      const cos = Math.abs(Math.cos(angleRad));
      const sin = Math.abs(Math.sin(angleRad));
      const bBoxW = img.naturalWidth * cos + img.naturalHeight * sin;
      const bBoxH = img.naturalWidth * sin + img.naturalHeight * cos;

      // Configuration du canvas
      canvas.width = bBoxW;
      canvas.height = bBoxH;

      const containerW = container.clientWidth;
      const containerH = container.clientHeight;
      const scale = Math.min(containerW / bBoxW, containerH / bBoxH);

      const displayWidth = bBoxW * scale;
      const displayHeight = bBoxH * scale;

      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${displayHeight}px`;

      const ctx = canvas.getContext('2d');

      // Amélioration de la qualité de rendu
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.clearRect(0, 0, bBoxW, bBoxH);
      ctx.save();
      ctx.translate(bBoxW / 2, bBoxH / 2);
      ctx.rotate(angleRad);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      ctx.restore();
    }
  }, [imageLoaded, rotationAngle]);

  /**
   * Appelée lorsque l'image est chargée.
   * Met à jour l'état "imageLoaded" en conséquence et force le redessin du canvas.
   */
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    // Force le redessin du canvas après un court délai pour s'assurer que l'image est bien chargée
    setTimeout(() => {
      drawCanvas();
    }, 100);
  }, [drawCanvas]);

  // Éclaircissement automatique au chargement
  useEffect(() => {
    const processInitialImage = async () => {
      if (src) {
        try {
          setIsProcessing(true);
          const isDark = await isImageTooDark(src);

          if (isDark) {
            showNotification('info', 'Image détectée comme sombre, éclaircissement automatique...', 2000);
            const lightenedBlob = await lightenImage(src);
            const lightenedUrl = URL.createObjectURL(lightenedBlob);
            setImageLoaded(false);
            setCurrentImageSrc(lightenedUrl);
            showNotification('success', 'Image éclaircie automatiquement !', 2000);
          } else {
            setCurrentImageSrc(src);
          }
        } catch (error) {
          console.warn('Erreur lors du traitement de l\'image:', error);
          setCurrentImageSrc(src);
        } finally {
          setIsProcessing(false);
        }
      }
    };

    processInitialImage();
  }, [src]);

  useEffect(() => {
    if (imageRef.current && imageRef.current.complete && imageRef.current.naturalWidth > 0) {
      handleImageLoad();
    }
  }, [currentImageSrc, handleImageLoad]); // Ajout de handleImageLoad dans les dépendances

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // Fonction pour éclaircir manuellement
  const handleManualLighten = async () => {
    if (currentImageSrc) {
      try {
        setIsProcessing(true);
        showNotification('info', 'Éclaircissement en cours...', 1000);

        const lightenedBlob = await lightenImage(currentImageSrc, 1.4);
        const lightenedUrl = URL.createObjectURL(lightenedBlob);

        // Forcer le rechargement de l'image
        setImageLoaded(false);
        setCurrentImageSrc(lightenedUrl);

        showNotification('success', 'Image éclaircie manuellement !', 2000);
      } catch (error) {
        showNotification('error', 'Erreur lors de l\'éclaircissement de l\'image.');
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const getRelativePosition = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();

    // Gestion des événements tactiles et souris
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      // Événement tactile
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if (e.changedTouches && e.changedTouches.length > 0) {
      // Événement tactile de fin
      clientX = e.changedTouches[0].clientX;
      clientY = e.changedTouches[0].clientY;
    } else {
      // Événement souris
      clientX = e.clientX;
      clientY = e.clientY;
    }

    // Calcul plus précis avec position relative
    const displayWidth = rect.width;
    const displayHeight = rect.height;
    const actualWidth = canvas.width;
    const actualHeight = canvas.height;

    // Position relative dans l'élément affiché
    const relativeX = (clientX - rect.left) / displayWidth;
    const relativeY = (clientY - rect.top) / displayHeight;

    // Conversion vers les coordonnées du canvas réel
    const canvasX = Math.max(0, Math.min(actualWidth, relativeX * actualWidth));
    const canvasY = Math.max(0, Math.min(actualHeight, relativeY * actualHeight));

    return { x: canvasX, y: canvasY };
  }, []);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    const pos = getRelativePosition(e);
    setDragStart(pos);
    setCrop({ ...pos, width: 0, height: 0 });
    setIsDragging(true);
  }, [getRelativePosition]);

  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    const pos = getRelativePosition(e);
    setDragStart(pos);
    setCrop({ ...pos, width: 0, height: 0 });
    setIsDragging(true);
  }, [getRelativePosition]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const pos = getRelativePosition(e);
    setCrop({
      x: Math.min(dragStart.x, pos.x),
      y: Math.min(dragStart.y, pos.y),
      width: Math.abs(pos.x - dragStart.x),
      height: Math.abs(pos.y - dragStart.y),
    });
  }, [isDragging, dragStart, getRelativePosition]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault(); // Empêche le scroll de la page
    const pos = getRelativePosition(e);
    setCrop({
      x: Math.min(dragStart.x, pos.x),
      y: Math.min(dragStart.y, pos.y),
      width: Math.abs(pos.x - dragStart.x),
      height: Math.abs(pos.y - dragStart.y),
    });
  }, [isDragging, dragStart, getRelativePosition]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  /**
   * Fonction appelée lorsque l'utilisateur confirme le découpage.
   * Crée un canvas contenant la zone de découpe de l'image originale,
   * puis convertit ce canvas en URL de données d'image JPEG, avant
   * d'appeler la fonction onCrop avec cette URL.
   * Si la zone de découpe est trop petite (moins de 10x10 pixels),
   * la fonction ne fait rien.
   */
  const handleCropConfirm = () => {
    if (crop.width > 10 && crop.height > 10) {
      const canvas = canvasRef.current;
      const croppedCanvas = document.createElement('canvas');
      croppedCanvas.width = crop.width;
      croppedCanvas.height = crop.height;
      const ctx = croppedCanvas.getContext('2d');

      // Ajout pour remplir le canvas avec du blanc
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, crop.width, crop.height);

      // Dessin de la partie sélectionnée
      ctx.drawImage(
        canvas,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        crop.width,
        crop.height
      );
      const croppedImageData = croppedCanvas.toDataURL('image/jpeg', 0.9);
      onCrop(croppedImageData);
    }
  };

  useEffect(() => {
    if (isDragging) {
      // Événements souris
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      // Événements tactiles
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Composant de notification
  const NotificationComponent = () => {
    if (!notification) return null;

    const getNotificationStyles = () => {
      const baseStyles = {
        success: { background: '#10b981', icon: '✓' },
        error: { background: '#ef4444', icon: '⚠' },
        info: { background: '#3b82f6', icon: 'ℹ' },
        warning: { background: '#f59e0b', icon: '⚠' }
      };
      return baseStyles[notification.type] || baseStyles.info;
    };

    const styles = getNotificationStyles();

    return (
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: styles.background,
        color: 'white',
        padding: '16px 24px',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        zIndex: 1002,
        maxWidth: '400px',
        fontSize: '16px',
        fontWeight: '500',
        animation: 'notificationSlideIn 0.3s ease-out'
      }}>
        <span style={{ fontSize: '18px' }}>{styles.icon}</span>
        <span>{notification.message}</span>
      </div>
    );
  };

  return (
    <div className="image-cropper-modal">
      <NotificationComponent />
      <div className="image-cropper-modal__content">
        <div className="image-cropper-modal__header">
          <h3 className="image-cropper-modal__header-title">Découper le tampon</h3>
          <button onClick={onCancel} className="image-cropper-modal__close-btn">
            <Close />
          </button>
        </div>

        <div className="image-cropper-modal__body">
          <div className="image-cropper-modal__instructions">
            Sélectionnez la zone à découper en cliquant et glissant sur l'image
            {/* <br /><em style={{ color: '#666', fontSize: '0.9em' }}>
              💡 Conseil : Vous pouvez d'abord éclaircir l'image si nécessaire, puis sélectionner la zone à découper
            </em> */}
          </div>

          <div className="image-cropper-modal__image-container">
            <div ref={containerRef} className="image-cropper-modal__crop-area">
              <canvas
                ref={canvasRef}
                className="image-cropper-modal__image"
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                style={{
                  opacity: isProcessing ? 0.6 : 1,
                  touchAction: 'none' // Empêche les gestes natifs du navigateur
                }}
              />

              {imageLoaded && canvasRef.current && (
                (() => {
                  const canvas = canvasRef.current;
                  const rect = canvas.getBoundingClientRect();

                  // Calcul plus précis de la position de la sélection
                  const displayWidth = rect.width;
                  const displayHeight = rect.height;
                  const actualWidth = canvas.width;
                  const actualHeight = canvas.height;

                  const scaleX = displayWidth / actualWidth;
                  const scaleY = displayHeight / actualHeight;

                  const selectionLeft = crop.x * scaleX;
                  const selectionTop = crop.y * scaleY;
                  const selectionWidth = crop.width * scaleX;
                  const selectionHeight = crop.height * scaleY;

                  return (
                    <div
                      className="image-cropper-modal__selection"
                      style={{
                        position: 'absolute',
                        left: `${canvas.offsetLeft + selectionLeft}px`,
                        top: `${canvas.offsetTop + selectionTop}px`,
                        width: `${selectionWidth}px`,
                        height: `${selectionHeight}px`,
                        display: crop.width > 0 && crop.height > 0 ? 'block' : 'none',
                        pointerEvents: 'none', // Empêche l'interférence avec les événements tactiles
                      }}
                    />
                  );
                })()
              )}
            </div>
          </div>

          <div className="image-cropper-modal__rotation-controls">
            <label>Rotation : {rotationAngle}°</label>
            <button
              onClick={() => setRotationAngle((prev) => (prev - 1 + 360) % 360)}
              className="rotation-fine-btn rotation-fine-btn--left"
              disabled={isProcessing}
              title="Rotation -1°"
            >
              ←
            </button>
            <button
              onClick={() => setRotationAngle((prev) => (prev + 1) % 360)}
              className="rotation-fine-btn rotation-fine-btn--right"
              disabled={isProcessing}
              title="Rotation +1°"
            >
              →
            </button>
            <input
              type="range"
              min="0"
              max="360"
              step="1"
              value={rotationAngle}
              onChange={(e) => setRotationAngle(Number(e.target.value))}
              className="rotation-slider"
              disabled={isProcessing}
            />
            <button
              onClick={() => setRotationAngle((prev) => (prev + 90) % 360)}
              className="rotation-btn"
              disabled={isProcessing}
            >
              Pivoter 90°
            </button>
            <button
              onClick={handleManualLighten}
              className="lighten-btn"
              disabled={isProcessing}
              title="Éclaircir l'image manuellement"
            >
              💡 Éclaircir
            </button>
          </div>

          <div className="image-cropper-modal__actions">
            <button onClick={onCancel} className="image-cropper-modal__cancel-btn">
              Annuler
            </button>
            <button
              onClick={handleCropConfirm}
              disabled={crop.width <= 10 || crop.height <= 10 || isProcessing}
              className="image-cropper-modal__crop-btn"
            >
              {crop.width > 10 && crop.height > 10 ? 'Découper' : 'Sélectionnez une zone à découper'}
            </button>
          </div>
        </div>

        <img
          ref={imageRef}
          src={currentImageSrc}
          alt="Zone à découper"
          style={{ display: 'none' }}
          onLoad={handleImageLoad}
          onError={(e) => {
            console.error('Erreur de chargement de l\'image:', e);
            setIsProcessing(false);
          }}
          key={currentImageSrc} // Force le rechargement quand l'URL change
        />
      </div>
    </div>
  );
};

export default ImageCropper;